import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure, useMediaQuery } from '@chakra-ui/react';
import {
  useClearNotificationsMutation,
  useNotificationListQuery,
  useViewNotificationMutation,
} from '@spartalabs/pdc-core';
import {
  FINES_PORTAL_NOTIFICATION_TYPE,
  FINES_PORTAL_ROUTE,
} from 'config/finesPortalNotification';
import { useCustomToast } from 'hooks';
import { useFinesBellNotifications } from 'hooks/useFinesBellNotifications';
import { DrawerCentral } from './components/DrawerCentral';
import { PopoverCentral } from './components/PopoverCentral';

export const NotificationCentral = () => {
  const [isMd] = useMediaQuery('(min-width: 768px)');
  const navigate = useNavigate();
  const [viewed, setViewed] = useState<string[]>([]);

  const { handleToast } = useCustomToast();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { data, isError, isFetching, isLoading, refetch } =
    useNotificationListQuery();

  const {
    items: finesNotifications,
    unreadCount: finesUnreadCount,
    isLoading: finesLoading,
    markViewed: markFineViewed,
    refetch: refetchFines,
  } = useFinesBellNotifications(isOpen);

  const mergedNotifications = useMemo(() => {
    const pdcItems = data || [];
    return [...pdcItems, ...finesNotifications];
  }, [data, finesNotifications]);

  const handleTotal = useMemo(() => {
    const totalPdc = data?.length || 0;
    const viewedPdc = viewed.length || 0;
    const pdcUnread = totalPdc > viewedPdc ? totalPdc - viewedPdc : 0;
    return pdcUnread + finesUnreadCount;
  }, [viewed, data, finesUnreadCount]);

  const [viewNot] = useViewNotificationMutation();
  const [clearNot, { isLoading: loadingAll }] = useClearNotificationsMutation();

  const handleView = useCallback(
    async ({
      id,
      type,
      isAll,
      plate,
      aitCode,
    }: {
      id?: string;
      type?: number;
      isAll?: boolean;
      plate?: string;
      aitCode?: string;
    }) => {
      try {
        if (isAll) {
          await clearNot().unwrap();
          await refetch();
          return;
        }

        if (!id || type == null) return;

        if (type === FINES_PORTAL_NOTIFICATION_TYPE) {
          markFineViewed(id);
          const query = aitCode
            ? `?ait=${encodeURIComponent(aitCode)}`
            : '';
          navigate(`${FINES_PORTAL_ROUTE}${query}`);
          onClose();
          return;
        }

        if (!viewed.includes(id)) {
          await viewNot({ id, type }).unwrap();
          setViewed(view => [...view, id]);
        }
        if (plate) navigate(`/gestao/veiculos/${plate}`);
      } catch {
        if (isAll)
          return handleToast({
            step: 4,
            action: 'limpar as notificações',
          });
      }
    },
    [viewed, clearNot, refetch, viewNot, navigate, markFineViewed, handleToast, onClose],
  );

  const handleRefetch = useCallback(() => {
    refetch();
    refetchFines();
  }, [refetch, refetchFines]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const listLoading =
    isFetching || isLoading || (isOpen && finesLoading);
  const listError = isError && mergedNotifications.length === 0;

  return isMd ? (
    <PopoverCentral
      total={handleTotal}
      notification={mergedNotifications}
      viewNot={handleView}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      clearAll={() => handleView({ isAll: true })}
      loadingClear={loadingAll}
      isLoading={listLoading}
      isError={listError}
      refetch={handleRefetch}
    />
  ) : (
    <DrawerCentral
      total={handleTotal}
      notification={mergedNotifications}
      viewNot={handleView}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      clearAll={() => handleView({ isAll: true })}
      loadingClear={loadingAll}
      isLoading={listLoading}
      isError={listError}
      refetch={handleRefetch}
    />
  );
};
